import React from 'react';
import {
  Box, Typography, Paper, Stack, Chip, Divider,
} from '@mui/material';
import {
  Extension as ExtensionIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { APP_LOGOS, APP_NAMES } from '../sharedMockData';

/**
 * Extension v5 — Background run (what the user SEES when a script runs)
 *
 * End-user surface. This is the frame-accurate mock of the entire visible
 * UX when a login script autoruns: essentially nothing. A tiny browser
 * notification toast appears in the bottom-right for ~4 seconds, then
 * fades. No sidebar, no progress bar, no step list. The auto-sign-in
 * happens invisibly on the partner's tab.
 *
 * We render a faux-desktop frame with a partner site visible + the toast
 * overlay so reviewers see the composition, not just the toast in
 * isolation.
 */

const TEAL = '#1ebea5';
const DEEP_TEAL = '#0b847f';

const MiniAchieveDxpIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16">
    <defs>
      <linearGradient id="bgrun-ok" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor={TEAL} />
        <stop offset="100%" stopColor={DEEP_TEAL} />
      </linearGradient>
    </defs>
    <rect x="1" y="1" width="14" height="14" rx="3" fill="url(#bgrun-ok)" />
    <path d="M 5 12 L 8 4 L 11 12 M 6.2 9.5 L 9.8 9.5"
          stroke="#fff" strokeWidth="1.2" fill="none"
          strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Toast = ({ appKey = 'khan', appLabel = APP_NAMES.khan || 'Khan Academy' }) => (
  <Paper elevation={8} sx={{
    width: 320, borderRadius: 2, overflow: 'hidden',
    bgcolor: '#263238', color: '#fff',
    border: '1px solid #455a64',
    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
    position: 'relative',
    // Slide-in animation to convey "just appeared"
    animation: 'slideIn 0.3s ease-out',
    '@keyframes slideIn': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
  }}>
    <Box sx={{ px: 1.25, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      <MiniAchieveDxpIcon size={18} />
      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#80cbc4', flexGrow: 1 }}>
        AchieveDXP
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', color: '#90a4ae' }}>
        now
      </Typography>
    </Box>
    <Box sx={{ px: 1.25, pb: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: 1, bgcolor: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', p: 0.5, flexShrink: 0,
      }}>
        <Box
          component="img"
          src={APP_LOGOS[appKey]}
          alt={appLabel}
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <CheckIcon sx={{ fontSize: 14, color: '#66bb6a' }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#fff' }}>
            Auto-signed into {appLabel}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: '0.68rem', color: '#b0bec5', mt: 0.25 }}>
          You're good to go. No action needed.
        </Typography>
      </Box>
    </Box>
    {/* auto-dismiss progress bar at bottom */}
    <Box sx={{
      height: 2, bgcolor: 'rgba(255,255,255,0.08)', position: 'relative',
    }}>
      <Box sx={{
        position: 'absolute', left: 0, top: 0, bottom: 0,
        bgcolor: TEAL,
        animation: 'countdown 4s linear forwards',
        '@keyframes countdown': {
          from: { width: '100%' }, to: { width: '0%' },
        },
      }} />
    </Box>
  </Paper>
);

const FauxDesktop = () => (
  <Paper elevation={4} sx={{
    width: '100%', maxWidth: 900, height: 460,
    borderRadius: 2, overflow: 'hidden', position: 'relative',
    bgcolor: '#fff', border: '1px solid #d5d9df',
    display: 'flex', flexDirection: 'column',
  }}>
    {/* Fake browser chrome */}
    <Box sx={{
      bgcolor: '#f1f3f4', px: 1.25, py: 0.75,
      display: 'flex', alignItems: 'center', gap: 0.75,
      borderBottom: '1px solid #dadce0',
    }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff5f56' }} />
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#27c93f' }} />
      <Box sx={{
        flexGrow: 1, mx: 2, px: 1.25, py: 0.3, bgcolor: '#fff',
        borderRadius: 3, border: '1px solid #dadce0',
        fontSize: '0.72rem', color: '#202124', fontFamily: 'system-ui',
      }}>
        khanacademy.org/profile/me
      </Box>
      {/* Pinned AchieveDXP icon */}
      <Box sx={{
        width: 20, height: 20, borderRadius: 0.5, overflow: 'hidden',
      }}>
        <MiniAchieveDxpIcon size={20} />
      </Box>
    </Box>

    {/* Fake khan academy page content */}
    <Box sx={{
      flexGrow: 1, display: 'flex', flexDirection: 'column',
      bgcolor: '#1865f2', color: '#fff', p: 2, position: 'relative',
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{
          width: 28, height: 28, bgcolor: '#fff', borderRadius: 0.75,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', p: 0.4,
        }}>
          <Box component="img" src={APP_LOGOS.khan} alt="Khan"
               sx={{ maxWidth: '100%', maxHeight: '100%' }}
               onError={(e) => { e.target.style.display = 'none'; }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Khan Academy</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{
          width: 28, height: 28, bgcolor: '#14bf96', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: '0.72rem',
        }}>
          JM
        </Box>
      </Stack>
      <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1, p: 2, mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.82rem', color: '#bbdefb', mb: 0.5 }}>
          Welcome back,
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>
          Jaden Martinez
        </Typography>
      </Box>
      <Box sx={{
        bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, p: 1.5,
        fontSize: '0.78rem', color: '#e3f2fd',
      }}>
        Pick up where you left off · Algebra 1 · Unit 3
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      <Typography sx={{ fontSize: '0.7rem', color: '#90caf9', textAlign: 'center' }}>
        (Faux Khan Academy landing page for mock context)
      </Typography>
    </Box>

    {/* Toast anchored bottom-right */}
    <Box sx={{
      position: 'absolute', bottom: 16, right: 16, zIndex: 5,
    }}>
      <Toast />
    </Box>
  </Paper>
);

const ExtV5BackgroundRunMockup = () => (
  <Box sx={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 3, p: 4, bgcolor: '#eef1f5', minHeight: 'calc(100vh - 180px)',
  }}>
    <Box sx={{ textAlign: 'center', maxWidth: 760 }}>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
        <ExtensionIcon sx={{ fontSize: 24, color: TEAL }} />
        <Typography variant="h6" fontWeight={700}>
          Background run — the entire user-visible UX
        </Typography>
        <Chip label="END USER" size="small" sx={{ fontWeight: 700, bgcolor: '#e8f5e9', color: '#1b5e20' }} />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        What a user sees when AchieveDXP auto-signs them into a partner site:
        essentially nothing. A small toast appears in the bottom-right for ~4
        seconds, then fades. The script itself runs invisibly on the partner tab.
        No sidebar opens, no step-by-step playback, no interaction is required.
      </Typography>
    </Box>

    <FauxDesktop />

    <Divider sx={{ width: '80%', my: 1 }} />

    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="subtitle2" sx={{ mb: 2, color: '#455a64' }}>
        Toast in isolation (with auto-dismiss progress bar)
      </Typography>
      <Toast />
    </Box>

    <Paper elevation={0} sx={{
      maxWidth: 720, p: 2, bgcolor: '#fff3cd',
      border: '1px solid #ffe082', borderRadius: 1.5,
    }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#5d4037', fontSize: '0.75rem' }}>
        TOAST POLICY
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#4e342e', mt: 0.5 }}>
        One toast per auto-sign-in event. Suppressed if another toast is already
        visible (queue-replace rather than stack). Failure variants ("Couldn't
        sign you in — please enter your password") use the same container with
        the red tint from the error-state popup. Toasts are never blocking and
        never steal focus.
      </Typography>
    </Paper>
  </Box>
);

export default ExtV5BackgroundRunMockup;
